import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Nav } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import ObigPurchaseDebtBar from "../components/ObigPurchaseDebtBar";
import CurrencySwitcher from "../components/CurrencySwitcher";
import MaterialsPage from "./MaterialsPage";
import SuppliersPage from "./SuppliersPage";
import PurchasesPage from "./PurchasesPage";
import MaterialsStockPage from "./MaterialsStockPage";
import WorkshopPayrollPage from "./WorkshopPayrollPage";
import ProductionDeliveriesPage from "./ProductionDeliveriesPage";
import ProductionReconciliationPage from "./ProductionReconciliationPage";
import TechCardsPage from "./TechCardsPage";

const MATERIALS_HUB_TABS = [
    { key: "purchases", label: "Закупки" },
    { key: "stock", label: "Склад" },
    { key: "tech-cards", label: "Тех. карти" },
    { key: "deliveries", label: "Поставки" },
    { key: "materials", label: "Матеріали" },
    { key: "suppliers", label: "Постачальники" },
    { key: "workshop-payroll", label: "Зарплати (цех)" },
    { key: "reconciliation", label: "Звірка" },
];

const MAIN_TAB_KEYS = new Set(MATERIALS_HUB_TABS.map((t) => t.key));

const PAYROLL_SUB_KEYS = new Set(["calc", "workshops", "history"]);

/** Зарплати цеху менеджеру закупок недоступні (сервер теж не віддає). */
const PURCHASE_MANAGER_HIDDEN_TABS = new Set(["workshop-payroll"]);

export default function MaterialsHubPage({ user }) {
    const { tab: tabParam, sub: subParam } = useParams();
    const navigate = useNavigate();

    const hubTabs = useMemo(
        () =>
            user?.role === "purchase_manager"
                ? MATERIALS_HUB_TABS.filter((t) => !PURCHASE_MANAGER_HIDDEN_TABS.has(t.key))
                : MATERIALS_HUB_TABS,
        [user?.role]
    );

    const tab = useMemo(() => {
        if (!tabParam) {
            return "purchases";
        }
        if (!hubTabs.some((t) => t.key === tabParam)) {
            return null;
        }
        return MAIN_TAB_KEYS.has(tabParam) ? tabParam : null;
    }, [tabParam, hubTabs]);

    useEffect(() => {
        if (tabParam && tab === null) {
            navigate("/materials/purchases", { replace: true });
        }
    }, [tabParam, tab, navigate]);

    useEffect(() => {
        if (!tab || tab === null) {
            return;
        }
        if (tab === "stock" || tab === "workshop-payroll") {
            return;
        }
        if (subParam) {
            navigate(`/materials/${tab}`, { replace: true });
        }
    }, [tab, subParam, navigate]);

    useEffect(() => {
        if (tab !== "workshop-payroll") {
            return;
        }
        if (subParam == null || subParam === "") {
            return;
        }
        if (!PAYROLL_SUB_KEYS.has(subParam)) {
            navigate("/materials/workshop-payroll", { replace: true });
        }
    }, [tab, subParam, navigate]);

    const selectMainTab = useCallback(
        (k) => {
            if (k === "stock") {
                navigate("/materials/stock");
            } else if (k === "workshop-payroll") {
                navigate("/materials/workshop-payroll");
            } else {
                navigate(`/materials/${k}`);
            }
        },
        [navigate]
    );

    const setStockSub = useCallback(
        (scopeKey) => {
            if (scopeKey === "all") {
                navigate("/materials/stock");
            } else {
                navigate(`/materials/stock/${scopeKey}`);
            }
        },
        [navigate]
    );

    const setPayrollSub = useCallback(
        (pTab) => {
            if (pTab === "calc") {
                navigate("/materials/workshop-payroll");
            } else {
                navigate(`/materials/workshop-payroll/${pTab}`);
            }
        },
        [navigate]
    );

    const stockUrlKey =
        tab === "stock"
            ? subParam === undefined || subParam === "all"
                ? "all"
                : subParam
            : "all";

    const payrollUrlTab =
        tab === "workshop-payroll"
            ? subParam === "workshops" || subParam === "history"
                ? subParam
                : "calc"
            : "calc";

    const [purchaseDebtUAH, setPurchaseDebtUAH] = useState(null);
    const [purchaseDebtBySupplier, setPurchaseDebtBySupplier] = useState([]);

    const [stockCurrency, setStockCurrency] = useState("UAH");
    const [usdBuyRate, setUsdBuyRate] = useState(null);
    const [usdSellRate, setUsdSellRate] = useState(null);

    useEffect(() => {
        const fetchPurchaseDebt = async () => {
            try {
                const res = await fetch("/api/purchases/debt-summary");
                if (!res.ok) {
                    throw new Error(String(res.status));
                }
                const data = await res.json();
                setPurchaseDebtUAH(
                    typeof data.totalDebtUAH === "number" ? data.totalDebtUAH : 0
                );
                setPurchaseDebtBySupplier(
                    Array.isArray(data.bySupplier) ? data.bySupplier : []
                );
            } catch (e) {
                console.error("Помилка боргу по закупках:", e);
                setPurchaseDebtUAH(null);
                setPurchaseDebtBySupplier([]);
            }
        };
        fetchPurchaseDebt();
    }, []);

    if (tabParam && tab === null) {
        return null;
    }

    const activeTab = tab || "purchases";

    return (

        <div className="container-fluid materials-hub mt-2 mt-md-3">

            <ObigPurchaseDebtBar
                totalDebtUAH={purchaseDebtUAH}
                bySupplier={purchaseDebtBySupplier}
                displayCurrency={stockCurrency}
                usdSellRate={usdSellRate}
                onOpenPurchasesTab={() => selectMainTab("purchases")}
            />

            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-md-between gap-3 materials-hub-top mb-3">
                <h3 className="mb-0 materials-hub-title">Виробництво</h3>
                <div className="materials-hub-currency w-100 w-md-auto ms-md-auto">
                    <CurrencySwitcher
                        compact
                        currency={stockCurrency}
                        setCurrency={setStockCurrency}
                        usdSellRate={usdSellRate}
                        usdBuyRate={usdBuyRate}
                        setUsdSellRate={setUsdSellRate}
                        setUsdBuyRate={setUsdBuyRate}
                    />
                </div>
            </div>

            <Form.Group className="d-md-none mb-3 materials-hub-tab-select">
                <Form.Label className="small text-muted mb-1">Розділ</Form.Label>
                <Form.Select
                    value={activeTab}
                    aria-label="Розділ виробництва"
                    onChange={(e) => selectMainTab(e.target.value)}
                >
                    {hubTabs.map((t) => (
                        <option key={t.key} value={t.key}>
                            {t.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            <div className="materials-hub-nav-scroll d-none d-md-block">
                <Nav
                    variant="tabs"
                    className="materials-hub-tabs flex-nowrap"
                    activeKey={activeTab}
                    onSelect={(k) => k && selectMainTab(k)}
                >
                    {hubTabs.map((t) => (
                        <Nav.Item key={t.key}>
                            <Nav.Link eventKey={t.key} role="tab">
                                {t.label}
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>

            <div className="materials-hub-panel">
            {activeTab === "purchases" && (
                <PurchasesPage
                    displayCurrency={stockCurrency}
                    usdSellRate={usdSellRate}
                />
            )}
            {activeTab === "stock" && (
                <MaterialsStockPage
                    stockCurrency={stockCurrency}
                    usdSellRate={usdSellRate}
                    urlStockScopeKey={stockUrlKey}
                    onStockScopeNavigate={setStockSub}
                />
            )}
            {activeTab === "tech-cards" && <TechCardsPage />}
            {activeTab === "deliveries" && <ProductionDeliveriesPage />}
            {activeTab === "materials" && <MaterialsPage />}
            {activeTab === "suppliers" && <SuppliersPage />}
            {activeTab === "workshop-payroll" && (
                <WorkshopPayrollPage
                    urlPayrollTab={payrollUrlTab}
                    onPayrollTabNavigate={setPayrollSub}
                />
            )}
            {activeTab === "reconciliation" && <ProductionReconciliationPage />}
            </div>

        </div>

    );
}
